import moment from "moment-timezone";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";

import { UserBasedDiscountTicketInfoSerializer } from "@app-contexts/user/features/discounts/data/types";
import {
  PublicCalendarTicketTypeSerializer,
  TicketTypeSerializer,
} from "../types/public";

/**
 * Returns true if the ticket type's sale end time is in the future
 * @param {object} ticketType
 * @return {boolean}
 */
export const isUpcomingSaleTicketType = (
  ticketType: TicketTypeSerializer, // uses the public ticket type serializer
  timezone: string
): boolean => {
  // compare the value of sale_ends_on to the current time
  // (the backend updates sale_ends_on to be the same as sale_ends_on_custom, so we don't need to check the custom field)
  return (
    moment(ticketType.sale_ends_on).tz(timezone).valueOf() >
    moment().tz(timezone).valueOf()
  );
};

/**
 * @param ticketType the ticket type to check
 * @param allowStartsOnBypass sale is allowed before starts on date
 * @return {boolean} Whether or not ticket type is currently available on sale
 */
export const isOnSaleTicketType = (
  ticketType: TicketTypeSerializer | SeatingItemPropertyMap,
  allowStartsOnBypass?: boolean
): boolean => {
  const dt = new Date();
  if (
    allowStartsOnBypass &&
    ticketType.bypass_sale_starts_on_box_office &&
    moment(ticketType.sale_starts_on).toDate() > dt
  ) {
    return true;
  }
  return (
    moment(ticketType.sale_starts_on).toDate() < dt &&
    moment(ticketType.sale_ends_on).toDate() > dt
  );
};

/**
 * True if waitlist is truthy and the status is = "active"
 * @param tt ticket type object
 * @returns {boolean}
 */
export const isTicketTypeWaitlisted = (
  tt?: TicketTypeSerializer | SeatingItemPropertyMap | null
): boolean => {
  return Boolean(tt?.waitlist && tt.waitlist.status === "active");
};

/**
 * True if unavailable
 *
 * @param tt ticket type object
 * @return {boolean|*}
 */
export const isTicketTypeSoldOut = (
  tt:
    | TicketTypeSerializer
    | SeatingItemPropertyMap
    | PublicCalendarTicketTypeSerializer
): boolean => {
  // TODO: implement seating map logic with sold out tickets

  // if (selectedPermissionsContainGA() && !tt.sold_out) {
  //   const permissions = locationPermissions.filter((lp) => lp.ticket_type === tt.id);
  //   return permissions.every((e) => e.stats && e.stats.fully_claimed === true);
  // }

  if (
    "is_available_for_quantity_requested" in tt &&
    (tt as PublicCalendarTicketTypeSerializer)
      .is_available_for_quantity_requested === false
  ) {
    return true;
  }

  return tt.sold_out;
};

type InventoryStatusType = {
  displayValue: boolean;
  inventoryValue: number | null | undefined;
};

/**
 * Displays or hides the LowInventoryStatus/RemainingTicketStatus components, based on the return value
 * Also gets the remaining amount of ticket inventory
 * @param tts ticket type object
 * @return {InventoryStatusType}
 */
export const ticketDisplayLowInventory = (
  tts: TicketTypeSerializer | SeatingItemPropertyMap
): InventoryStatusType => {
  const totalAmountLeft = tts.inventory_left;
  const showRemainingTickets = Boolean(totalAmountLeft);

  const returnValue = {
    displayValue: showRemainingTickets,
    inventoryValue: totalAmountLeft,
  };

  return returnValue;
};

// returns a single applicable voucher for the input ticket type
export const voucherForTT = (
  type: TicketTypeSerializer | SeatingItemPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[]
): UserBasedDiscountTicketInfoSerializer | undefined => {
  return userVouchers?.find((voucher) =>
    voucher.$applicableTTs?.includes(type.id)
  );
};

// returns an applicable voucher code for the input ticket type
export const voucherCodeForTT = (
  type: TicketTypeSerializer | SeatingItemPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[]
): string | null => {
  const voucher = voucherForTT(type, userVouchers);

  if (voucher) {
    return voucher.code;
  }

  return null;
};

// returns a list of applicable vouchers for the input ticket type
export const voucherListForTT = (
  type: TicketTypeSerializer | SeatingItemPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[]
): UserBasedDiscountTicketInfoSerializer[] => {
  return userVouchers.filter((voucher) =>
    voucher.$applicableTTs?.includes(type.id)
  );
};

// returns true if no voucher is required to buy the input ticket type
// else returns a boolean value based on whether the user has an applicable voucher for the ticket type
export const voucherNotRequired = (
  type: TicketTypeSerializer | SeatingItemPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[]
): boolean => {
  // Block the ticket type from being added to cart if
  // TT requires a voucher and user has no voucher for the TT
  if (type.voucher_purchases_only) {
    if (userVouchers) {
      return userVouchers.some(
        (voucher: UserBasedDiscountTicketInfoSerializer) =>
          voucher.$applicableTTs?.includes(type.id)
      );
    }
    // else return false
    return false;
  }
  return true;
};

export const cleanTicketType = (
  ticketType: TicketTypeSerializer,
  isDynamicHoldTicketType?: boolean
): TicketTypeSerializer => {
  const soldOutObj = {
    sold_out: ticketType.sold_out && !isDynamicHoldTicketType,
  };
  Object.assign(ticketType, soldOutObj);
  return ticketType;
};

/**
 * Filters ticket types by IDs
 * @param ticketTypes Array of ticket types
 * @param ticketTypeIds Array of ticket type IDs to filter by
 * @returns Filtered array of ticket types
 */
export function filterTicketTypes(
  ticketTypes: TicketTypeSerializer[],
  ticketTypeIds: number[]
): TicketTypeSerializer[] {
  if (!ticketTypes || !ticketTypeIds.length) return ticketTypes;
  return ticketTypes.filter((tt) => ticketTypeIds.includes(tt.id));
}
